package com.evoapps.evoshift.domain.usecase.reports

import com.evoapps.evoshift.domain.model.payableTimes
import com.evoapps.evoshift.domain.model.breakMinutes
import com.evoapps.evoshift.domain.model.unpaidBreaks

import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.TimesheetHoursSource
import com.evoapps.evoshift.domain.model.WorkingTimeDay
import com.evoapps.evoshift.domain.model.WorkingTimeEntry
import com.evoapps.evoshift.domain.model.WorkingTimeSheet
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant

/**
 * The month's working-time records: one sheet per person and workplace, for everyone active at
 * the given workplaces (also those without shifts: the record covers everyone employed) and
 * anyone who worked there, or just [userId]. Published or clocked shifts count; drafts do not.
 */
class GenerateWorkingTimeReportUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val absenceRepository: AbsenceRepository
) {
    suspend operator fun invoke(
        workplaceIds: List<String>,
        year: Int,
        month: Int,
        userId: String? = null
    ): Result<List<WorkingTimeSheet>> = runCatching {
        workplaceIds.distinct().flatMap { workplaceId ->
            val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
            val rules = PayrollRules.of(workplace.settings.market)
            val shifts = shiftRepository.getShiftsForRoster(RosterIds.of(workplaceId, year, month)).getOrDefault(emptyList())
                .filter { it.userId != null && (it.isPublished || it.actualTime?.end?.isNotEmpty() == true) }
            val team = userRepository.getAssignableEmployees(workplaceId).getOrDefault(emptyList())
            val people = (team.map { it.uid } + shifts.map { it.userId!! }).distinct()
                .filter { userId == null || it == userId }
            val absences = absenceRepository.getAbsencesForWorkplace(workplaceId).getOrDefault(emptyList())
                .filter { it.status == AbsenceStatus.APPROVED }
            val days = monthDays(year, month)
            val options = workplace.settings.reports.timesheet

            people.map { uid ->
                val profile = team.firstOrNull { it.uid == uid } ?: userRepository.getUserProfile(uid).getOrNull()
                val theirShifts = shifts.filter { it.userId == uid }
                val theirAbsences = absences.filter { it.userId == uid }
                WorkingTimeSheet(
                    userId = uid,
                    employeeName = profile?.displayName ?: uid,
                    role = profile?.engagements?.get(workplaceId)?.role,
                    workplaceId = workplaceId,
                    workplaceName = workplace.name,
                    year = year,
                    month = month,
                    normHours = rules.workingHours(year, month),
                    options = options,
                    days = days.map { day ->
                        val date = day.toString()
                        WorkingTimeDay(
                            date = date,
                            isSunday = day.dayOfWeek == DayOfWeek.SUNDAY,
                            isHoliday = rules.isHoliday(day),
                            shifts = theirShifts.filter { it.date == date }.sortedBy { it.plannedTime.start }.mapNotNull { shift ->
                                val actual = shift.actualTime?.takeIf { it.start.isNotEmpty() && it.end.isNotEmpty() }
                                // SH-258: "registered hours only" leaves out the shifts without a registration.
                                if (options.hoursSource == TimesheetHoursSource.ACTUAL && actual == null) return@mapNotNull null
                                val planned = options.round(shift.plannedTime.start, shift.plannedTime.end)
                                val clocked = actual?.let { options.round(it.start, it.end) }
                                // SH-134: the hours count the payable times (from the plan where the workplace says so);
                                // SH-258: or the plan alone, and rounded as printed.
                                val payable = if (options.hoursSource == TimesheetHoursSource.PLAN) null else shift.payableTimes(workplace.settings.clockReview)
                                val (start, end) = options.round(
                                    LocalDateTime.parse(payable?.start ?: shift.plannedTime.start),
                                    LocalDateTime.parse(payable?.end ?: shift.plannedTime.end)
                                )
                                val unpaid = if (payable != null) shift.unpaidBreaks(workplace.settings.breakPolicy, workplace.settings.clockReview) else emptyList()
                                WorkingTimeEntry(
                                    plannedStart = planned.first,
                                    plannedEnd = planned.second,
                                    actualStart = clocked?.first,
                                    actualEnd = clocked?.second,
                                    // SH-116: the unpaid break is not working time.
                                    hours = (hoursBetween(start, end) - unpaid.sumOf { hoursBetween(it.start, it.end) }).coerceAtLeast(0.0),
                                    nightHours = (rules.nightHours(start, end, workplace.settings.payroll) -
                                        unpaid.sumOf { rules.nightHours(it.start, it.end, workplace.settings.payroll) }).coerceAtLeast(0.0),
                                    breakMinutes = if (actual != null) shift.breakMinutes() else 0
                                )
                            },
                            absence = theirAbsences.firstOrNull { date in it.startDate..it.endDate }?.type
                        )
                    }
                )
            }
        }.sortedWith(compareBy({ it.workplaceName.lowercase() }, { it.employeeName.lowercase() }))
    }

    private fun monthDays(year: Int, month: Int): List<LocalDate> =
        generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
            .takeWhile { it.month.ordinal + 1 == month }
            .toList()

    private fun hoursBetween(start: LocalDateTime, end: LocalDateTime): Double =
        (end.toInstant(TimeZone.UTC) - start.toInstant(TimeZone.UTC)).inWholeMinutes / 60.0
}
